import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { LogoMark } from "@/components/project/Primitives";
import { Button } from "@/components/ui/button";
import { formatLegalHtml } from "@/lib/legalDocuments";
import { SERVICE_AGREEMENT_TEXT } from "@/content/agreements/serviceAgreement";
import { DATA_PROCESSING_AGREEMENT_TEXT } from "@/content/agreements/dataProcessingAgreement";
import { DPA_META, SERVICE_AGREEMENT_META } from "@/lib/platformAgreements";
import { apiPost } from "@/api/client";
import { useProjectLocal } from "@/context/AppContext";

export const PLATFORM_AGREEMENT_DOCS = {
  "service-agreement": {
    ...SERVICE_AGREEMENT_META,
    body: SERVICE_AGREEMENT_TEXT,
    downloadName: "BuildHof_Service_Agreement.pdf",
  },
  "data-processing-agreement": {
    ...DPA_META,
    body: DATA_PROCESSING_AGREEMENT_TEXT,
    downloadName: "Data_Processing_Agreement.pdf",
  },
};

export function PlatformAgreementPage() {
  const { docId } = useParams();
  const doc = PLATFORM_AGREEMENT_DOCS[docId];
  const { isAuthenticated } = useProjectLocal();

  useEffect(() => {
    if (!doc || !isAuthenticated) return;
    apiPost("/onboarding/agreements/view", {
      document_id: doc.id,
      source: "full_text_page",
    }).catch(() => {});
  }, [doc, isAuthenticated]);

  if (!doc) return <Navigate to="/onboarding/agreements" replace />;

  const html = formatLegalHtml(doc.body, { title: doc.title });

  return (
    <main className="page-shell min-h-screen bg-[#F8F5EE]" data-testid="platform-agreement-page">
      <header className="border-b bg-white/90 backdrop-blur" data-testid="platform-agreement-header">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <LogoMark />
          <div className="flex flex-wrap items-center gap-2">
            <a href={doc.pdfHref} download={doc.downloadName} data-testid="platform-agreement-download">
              <Button type="button" variant="outline" className="tap-target rounded-lg">
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </a>
            <Link to="/onboarding/agreements" data-testid="platform-agreement-back-link">
              <Button type="button" className="tap-target rounded-lg">
                <ArrowLeft className="h-4 w-4" />
                Back to agreements
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 py-8 md:py-12" data-testid="platform-agreement-article">
        <p className="text-xs font-extrabold uppercase tracking-wide text-[#4a7a5e]" data-testid="platform-agreement-kicker">
          BuildHof legal
        </p>
        <h1 className="mt-3 text-3xl font-extrabold leading-tight text-[#173B2F] sm:text-4xl" data-testid="platform-agreement-title">
          {doc.title}
        </h1>
        <p className="mt-2 text-sm font-semibold text-[#173B2F]" data-testid="platform-agreement-price">
          {doc.price_label}
        </p>
        <p className="mt-1 text-xs text-muted-foreground" data-testid="platform-agreement-effective">
          {doc.effective_label}
        </p>
        <p className="mt-3 text-sm leading-6 text-muted-foreground" data-testid="platform-agreement-summary">
          {doc.summary}
        </p>
        <div
          className="mt-8 rounded-2xl border bg-white p-5 shadow-sm sm:p-8 [&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-extrabold [&_h1]:text-[#173B2F] [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-lg [&_h2]:font-extrabold [&_h2]:text-[#173B2F] [&_p]:mb-4 [&_p]:text-[15px] [&_p]:leading-7 [&_p]:text-[#2c2c2a] [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_li]:text-[15px] [&_li]:leading-7 [&_li]:text-[#2c2c2a]"
          data-testid="platform-agreement-body"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </article>
    </main>
  );
}
